import * as React from 'react';
import styled from 'styled-components';
import store from 'store';
import { CustomPicker, CustomPickerProps, ColorResult, RGBColor } from 'react-color';
import { 
    Alpha, // слайдер указания прозрачности
    Saturation, // - сам холст выбора цвета
    Hue, // - горизонтальная палитра цветов
} from 'react-color/lib/components/common';
import { pipe, tuple } from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import * as Theme from '../../serviceTheme';
import * as I from '../../icons';
import * as Btn from '../Button';
import { Value } from './types';
import { useGetter, useEffect } from './slots';

type HEXColorString = string;
type RGBAColorString = string;

type Props = {
    className?: string,
}


// -------------------------------------------------------------------------------------
// Helpers 
// -------------------------------------------------------------------------------------

const isValueForColor = (x: any): x is { color: RGBColor } => 
    pipe(
        x,
        x => tuple(typeof(x) === 'object' && x !== null, x),
        ([s, x]) => s && typeof(x.color) === 'object',
    );

const getOpacityInput = (rgb: RGBColor) => 
    pipe(
        rgb,
        (r) => r !== undefined ? O.some(r) : O.none,
        O.chain(r => typeof(r.a) === 'number' ? O.some(r.a) : O.none),
        O.map(x => Math.round(x * 100)),
        O.fold(
            () => '-%',
            (x) => `${x}%`,
        ),
    );

const colorStringFromRGB = (c: RGBColor): RGBAColorString => `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})`;

const colorHEXFrom = (c: RGBColor): HEXColorString => {
    const r = (c.r).toString(16);
    const g = (c.g).toString(16);
    const b = (c.b).toString(16);

    return `#${r}${g}${b}`;
};

const takeFrom3hex = (c: string) => {
    return tuple(
        parseInt(c[1] + c[1], 16),
        parseInt(c[2] + c[2], 16),
        parseInt(c[3] + c[3], 16),
    );
}

const takeFrom6hex = (c: string) => {
    return tuple(
        parseInt(c.slice(1, 3), 16),
        parseInt(c.slice(3, 5), 16),
        parseInt(c.slice(5, 7), 16),
    );
}

const takeRGB = (c: string) => {
    switch(c.length){
        case 4: return takeFrom3hex(c);
        case 7: return takeFrom6hex(c);
        default: return takeFrom6hex(c);
    }
}

const colorStringToRGB = (c: string, opacity?: number): RGBColor => {
    const [_r, _g, _b] = takeRGB(c);

    return {
        r: isNaN(_r) ? 0 : _r,
        g: isNaN(_g) ? 0 : _g,
        b: isNaN(_b) ? 0 : _b,
        a: opacity || 1,
    }
}

const colorStringRGB2RGB = (c: RGBAColorString): RGBColor => {

    const [_r, _g, _b, _a] = c
        .replace(/^rgba\(/, '')
        .replace(/\)$/, '')
        .split(', ')
        .map(x => Number(x));

    return {
        r: isNaN(_r) ? 0 : _r,
        g: isNaN(_g) ? 0 : _g,
        b: isNaN(_b) ? 0 : _b,
        a: isNaN(_a) ? 1 : _a,
    }
}


export const utils = {
    stringRGB2ObjectRGB: colorStringRGB2RGB,
    stringHEX2ObjectRGB: colorStringToRGB,
    objectRGB2StringHEX: colorHEXFrom,
    objectRGB2StringRGB: colorStringFromRGB,
}



// -------------------------------------------------------------------------------------
// Palitra
// -------------------------------------------------------------------------------------

const AlphaPointerC = () => {
    return (
        <AlphaPointerWrapper>
            <AlphaPointer />
        </AlphaPointerWrapper>
    );
}

const HuePointerC = () => {
    return (
        <HuePointerWrapper>
            <HuePointer />
        </HuePointerWrapper>
    );
}

const Palitra = (props: CustomPickerProps<any>) => {
    return (
        <GridPalitra>
            <SaturationWrapper>
                <Saturation {...props}  />
            </SaturationWrapper>
            <HueWrapper>
                <Hue 
                    direction="vertical"
                    {...props}
                    pointer={HuePointerC}
                />
            </HueWrapper>
            <AlphaWrapper>
                <Alpha 
                    {...props} 
                    pointer={AlphaPointerC}
                />
            </AlphaWrapper>
        </GridPalitra>
    );
}

const Pointer = styled.div`
    border: 1px solid #fff;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    position: relative;
    width: 8px;
    height: 8px;
    background-color: transparent;
    box-shadow: 0 0 1px 1px #aaa;

    &:after {
        content: '';
        display: block;
        border-radius: 50%;
        position: absolute;
        transform: translate(-50%, -50%);
        top: 50%;
        left: 50%;
        width: 6px;
        height: 6px;
        box-shadow: inset 0 0 1px 1px #aaa;
    }
`;


const AlphaPointerWrapper = styled.div`
    height: 20px;
`;


const AlphaPointer = styled(Pointer)`
    top: 50%;
`;


const HuePointerWrapper = styled.div`
    width: 20px;
`;

const HuePointer = styled(Pointer)`
    left: 50%;
`;

const Cell = styled.div`
    position: relative;
    overflow: hidden;
    border-radius: 3px;
`;

const SaturationWrapper = styled(Cell)`
    position: relative;
    grid-area: saturation;
    padding-bottom: 100%;
`;

const HueWrapper = styled(Cell)`
    position: relative;
    grid-area: hue;
`;

const AlphaWrapper = styled(Cell)`
    position: relative;
    grid-area: alpha;
`;

const GridPalitra = styled.div`
    display: grid;
    grid-template-areas: 
        "saturation hue"
        "alpha hue";
    grid-template-rows: max-content 20px;
    grid-template-columns: 1fr 20px;
    grid-gap: 10px;
`;




const HOCPalitra = CustomPicker(Palitra);

const useColorInput = (color: RGBColor, handleChange: (color: RGBColor) => void) => {
    const [renderColor, updateColor] = React.useState(colorHEXFrom(color));
    const [renderOpacity, updateOpacity] = React.useState(getOpacityInput(color));
    const [isFocus, updateFocus] = React.useState(false);

    React.useEffect(() => {
        updateOpacity(getOpacityInput(color));
        if(!isFocus){
            updateColor(colorHEXFrom(color));
        }
    }, [color, isFocus]);

    const handlerColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const x = e.target.value;

        updateColor(x);
        handleChange(colorStringToRGB(x, color.a));
    }

    const handlerFocus = () => {
        updateFocus(true);
    }

    const handlerBlur = () => {
        updateFocus(false);
    }

    return {
        opacity: renderOpacity,
        hexInputProps: {
            value: renderColor,
            onChange: handlerColorChange,
            onFocus: handlerFocus,
            onBlur: handlerBlur,
        }
    };
}

type ColorsHeapProps = {
    colors: RGBAColorString[],
    onClick: (color: string) => void,
}

const ColorHeap = ({ colors, onClick }: ColorsHeapProps) => {

    const handler = (color: RGBAColorString) => () => {
        onClick(color);
    }

    return (
        <ColorGrid>
            {colors.map((color, index) => {
                return (
                    <ColorCell key={index} onClick={handler(color)}>
                        <ColorCellInner style={{ backgroundColor: color }} />
                    </ColorCell>
                );
            })}
        </ColorGrid>
    );
}

const ColorCellInner = styled.div`
    padding-bottom: 100%;
    border: 1px solid #f5f5f5;
`;

const ColorCell = styled.button`
    border-radius: 3px;
    overflow: hidden;
    border: 1px solid #C9CCD5;
    background-color: transparent;

    &:hover {
        box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.14);
    }
`;

const ColorGrid = styled.div`
    display: grid;
    grid-gap: 10px; 
    grid-template-columns: repeat(auto-fill,minmax(25px,1fr));
    grid-auto-rows: min-content;
    position: absolute;
    top: 0;
    left: 0;
    right: 10px;
    height: 100%;
`;

const initColorsHeap = [
    'rgba(255, 255, 255, 1)', 'rgba(23, 58, 85, 1)', 'rgba(255, 91, 92, 1)', 'rgba(82, 113, 255, 1)', 
    'rgba(57, 164, 250, 1)', 'rgba(84, 111, 122, 1)', 'rgba(0, 183, 109, 1)', 'rgba(255, 208, 57, 1)', 
    'rgba(0, 0, 0, 1)', 'rgba(255, 68, 78, 1)', 'rgba(61, 79, 249, 1)',
];


const initColors = (arr?: any): RGBAColorString[] => {
    return pipe(
        arr,
        O.fromNullable,
        O.chain(xs => xs && xs.colors ? O.some(xs.colors) : O.none),
        O.chain(xs => xs.length === 0 ? O.none : O.some(xs)),
        O.fold(
            () => initColorsHeap,
            (xs) => xs,
        ),
    );
}

const appendColor = (value: Record<string, any> | null) => 
    (xc: RGBAColorString[]): RGBAColorString[] => pipe(
        value,
        O.fromNullable,
        O.chain(x => x.color ? O.some(x.color) : O.none),
        O.map(colorStringFromRGB),
        O.fold(
            () => xc,
            (color) => xc.includes(color) 
                ? xc 
                : [...xc, color],
        ),
    );

const setInStorage = (colors: RGBAColorString[]) => {
    return { 
        colors,
    }
}

const PALITRA = 'dashboard_palitra';

const Component = ({ className }: Props) => {
    const service = useGetter('service');
    const target = useGetter('target');
    const dropdownController = useGetter('dropdown');

    const [changeValue] = useEffect('changeValue');

    const backgroundColor = React.useMemo(() => pipe(
        target.getValue(),
        x => isValueForColor(x) ? O.some(x) : O.none,
        O.fold(
            () => ({ r: 10, g: 20, b: 30, a: 1 }),
            (x) => x.color,
        ),
    ), [changeValue]);

    const startColors = React.useMemo(initColors, []);

    const [colors, updateColors] = React.useState(startColors);


    const handleChangeColor = React.useCallback((color: ColorResult) => {
        target.change({ color: color.rgb });
    }, []);

    const handleChange = React.useCallback((color: RGBColor) => {
        target.change({ color });
    }, []);

    const handleClick = React.useCallback((color: RGBAColorString) => {
        target.change({ color: colorStringRGB2RGB(color) });
    }, []);

    const handleClear = React.useCallback(() => {
        const { onSelect, name } = service;
        const emptyColor = { r: 255, g: 255, b: 255, a: 0 };

        target.change({ color: emptyColor });
        onSelect(target.getValue() as Value, name);
        dropdownController.close();
    }, []);

    const handleAccept = React.useCallback(() => {
        const { onSelect, name } = service;
        const { getValue } = target;

        const value = getValue() as Value;

        onSelect(value, name);
        updateColors(appendColor(value));
        dropdownController.close();
    }, []);
    
    React.useEffect(() => {
        const colorsFromStorage = store.get(PALITRA);
        updateColors(initColors(colorsFromStorage));
    }, []);

    React.useEffect(() => {
        store.set(PALITRA, setInStorage(colors));
    }, [colors]);

    const { hexInputProps } = useColorInput(backgroundColor, handleChange);

    return (
        <Container className={className}>
            <ContainerPalitra>
                <LeftBlock>
                    <ColorHeap
                        onClick={handleClick}
                        colors={colors}
                    />
                </LeftBlock>
                <RightBlock>
                    <HOCPalitra 
                        onChange={handleChangeColor}
                        color={backgroundColor}
                    />
                    <ValuesPanel>
                        <ValueInput {...hexInputProps} />
                        <Btn.Primary 
                            className="color_btn-accept"
                            onClick={handleAccept}>
                            Выбрать
                        </Btn.Primary>
                        <Reset onClick={handleClear}>
                            <I.Delete />
                        </Reset>
                    </ValuesPanel>
                </RightBlock>
            </ContainerPalitra>
        </Container>
    );
};

export default Component;


const Column = styled.div`
`;

const LeftBlock = styled(Column)`
    display: grid;
    grid-template-rows: 1fr max-content;
    border-right: 1px solid #f0f0f0;
    padding-right: 10px;
    position: relative;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 4px;
        height: 4px;
        padding: 1px;
    }

    &::-webkit-scrollbar-thumb {
        background: ${Theme.safeGetPrimary};
        border-radius: 40px 0px 0px 40px;
    }
`;
const RightBlock = styled(Column)`
`;

const ContainerPalitra = styled.div`
    display: grid;
    grid-template-columns: .4fr .6fr;
    padding: 10px;
    grid-gap: 10px;
`;


const ValuesPanel = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr max-content;
    grid-gap: 0 10px;
    margin-top: 10px;
`;


const ValueInput = styled.input`
    border: 1px solid #C9CCD5;
    height: 100%;
    width: 100%;
    padding-left: 6px;
    padding-right: 6px;
`;

const Reset = styled.button`
    background-color: transparent;
    display: flex;

    svg {
        width: 20px;
        height: 100%;
        fill: #918EAE;
    }

    &:hover {
        svg {
            fill: #222C35;
        }
    }
`;

const Container = styled.div`
    .sketch-picker {
        box-shadow: none !important;
        width: auto !important;
    }
`;
